import { MessageResultError } from '@getvim-os/errors';
import {
  AppToOsAwaitedMessage,
  AppToOsAwaitedMessageEvent,
  AppToOsAwaitedMessagePort,
  AppToOsMessageData,
  AppToOSMessageTypes,
  eventSchemas,
  HandshakePayload,
  osMessageResponseTypeSchema,
} from '@getvim/vim-os-api';
import { ErrorMessageResponseType } from '@getvim/vim-os-sdk-api';
import NotAllowedError from '../errors/notAllowedError';
import { SdkLogger } from '../sdkLogger';

const MESSAGE_RESPONSE_TIMEOUT = 30_000; // 30 seconds

export class OsCommunicator {
  private messagePort: MessagePort;
  constructor(
    _handshakePayload: HandshakePayload,
    osMessageChannel: MessageChannel,
    private logger?: SdkLogger,
    private allowedMessageTypes = Object.keys(AppToOSMessageTypes),
  ) {
    this.messagePort = osMessageChannel.port1;
  }

  public sendMessageToOS(message: AppToOsMessageData) {
    this.verifyMessageAllowed(message);
    return this.messagePort.postMessage(message);
  }

  private parseMessage<T = unknown>(eventType: string, message: AppToOsAwaitedMessageEvent<T>): T {
    if (message.error) {
      if (osMessageResponseTypeSchema.safeParse(message.response?.type).success) {
        throw message.response;
      } else {
        throw {
          type: ErrorMessageResponseType.internal_error,
          data: new MessageResultError(eventType, message.response),
          code: 500,
        };
      }
    } else {
      return message.response;
    }
  }

  public sendLongLivingMessageToOS<
    M extends keyof typeof eventSchemas, // M is the type of outgoing message to vim-os
    R = unknown, // R is the type of expected response form vim-os
  >(data: AppToOsMessageData<M>, callback: (message: AppToOsAwaitedMessage<R>) => void) {
    this.verifyMessageAllowed(data);
    const dataChannel = new MessageChannel();
    const managementChannel = new MessageChannel();
    const incomingDataPort = dataChannel.port1 as AppToOsAwaitedMessagePort<M>;
    const incomingManagementPort = managementChannel.port1 as AppToOsAwaitedMessagePort<M>;
    const outgoingDataPort = dataChannel.port2;
    const outgoingManagementPort = managementChannel.port2;
    incomingDataPort.start();
    incomingManagementPort.start();
    const typedListener = (event: { data: AppToOsAwaitedMessageEvent<R> }) => {
      try {
        const result = this.parseMessage<R>(data.type, event.data);
        callback(result);
      } catch (error: any) {
        callback({
          type: 'error',
          payload: error,
        });
        this.logger?.error('error with message from vim-os', {
          message: event.data,
          error,
        });
      }
    };
    incomingDataPort.addEventListener('message', typedListener);
    incomingManagementPort.addEventListener('message', typedListener);
    this.messagePort.postMessage(
      { ...data, managementPort: outgoingManagementPort, dataPort: outgoingDataPort },
      [outgoingManagementPort, outgoingDataPort],
    );
    return () => {
      incomingDataPort.removeEventListener('message', typedListener);
      incomingDataPort.close();
      incomingManagementPort.removeEventListener('message', typedListener);
      incomingManagementPort.close();
    };
  }

  public async sendAwaitedMessage<T = unknown>(
    data: AppToOsMessageData,
    timeout = MESSAGE_RESPONSE_TIMEOUT,
  ): Promise<T> {
    this.verifyMessageAllowed(data);
    const { type } = data;
    return await new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.logger?.error('OsCommunicator timed out waiting for response from Vim-OS', { data });
        reject({
          type: ErrorMessageResponseType.internal_error,
          data: `Did not receive response from Vim-OS for ${timeout}`,
          code: 500,
        });
      }, timeout);

      const messageChannel = new MessageChannel();
      const dataPort = messageChannel.port2;
      messageChannel.port1.addEventListener('message', (event) => {
        clearTimeout(timeoutId);
        try {
          const result = this.parseMessage<T>(type, event.data);
          resolve(result);
        } catch (error) {
          reject(error);
        }
        messageChannel.port1.close();
      });
      messageChannel.port1.start();
      this.messagePort.postMessage({ ...data, dataPort }, [dataPort]);
    });
  }

  private verifyMessageAllowed(message: AppToOsMessageData) {
    if (!this.allowedMessageTypes.includes(message.type)) {
      this.logger?.error('OsCommunicator received message of unknown type', { message });
      throw new NotAllowedError(message.type);
    }
  }
}
